/**
 * JERARCA LUXE — micro-3D tilt on product cards.
 * Only on hover-capable fine pointers, respects prefers-reduced-motion.
 * ~1 KB, deferred, never in the critical path.
 */
const fine = matchMedia('(hover: hover) and (pointer: fine)');
const motionOK = matchMedia('(prefers-reduced-motion: no-preference)');

const MAX_Y = 5; /* deg, left-right */
const MAX_X = 4; /* deg, up-down */

function attach(card) {
  card.dataset.jt = '1';
  card.classList.add('jerarca-tilt');
  let raf = 0;

  card.addEventListener('pointermove', (e) => {
    const r = card.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      card.style.setProperty('--jt-y', (x * MAX_Y).toFixed(2) + 'deg');
      card.style.setProperty('--jt-x', (-y * MAX_X).toFixed(2) + 'deg');
    });
  });

  card.addEventListener('pointerleave', () => {
    cancelAnimationFrame(raf);
    card.style.setProperty('--jt-x', '0deg');
    card.style.setProperty('--jt-y', '0deg');
  });
}

/**
 * Lazy video playback: backdrop videos carry no autoplay attribute, so the
 * browser only fetches metadata upfront. Playback (and the full download)
 * starts when the video nears the viewport, and pauses when it leaves.
 */
const videoObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const video = entry.target;
            if (entry.isIntersecting) {
              video.play().catch(() => {});
            } else if (!video.paused) {
              video.pause();
            }
          }
        },
        { rootMargin: '200px 0px' }
      )
    : null;

function initVideos(root) {
  const videos = (root || document).querySelectorAll('video[data-jerarca-video]:not([data-jv])');
  videos.forEach((video) => {
    video.dataset.jv = '1';
    if (!motionOK.matches) {
      return; /* reduced motion: leave the poster, never autoplay */
    }
    if (videoObserver) {
      videoObserver.observe(video);
    } else {
      video.play().catch(() => {});
    }
  });
}

/**
 * Trust-bar stagger reveal — fallback only.
 * Modern browsers animate the badges with native scroll-driven CSS
 * (animation-timeline: view()), so this runs ONLY where that's unsupported.
 * Adds .jerarca-trust--in once when the strip enters the viewport, then stops
 * observing (no repeat on scroll). Respects prefers-reduced-motion.
 */
const nativeScrollTimeline =
  typeof CSS !== 'undefined' && CSS.supports && CSS.supports('animation-timeline: view()');

function initTrustBar(root) {
  if (nativeScrollTimeline || !motionOK.matches) return;
  const bars = (root || document).querySelectorAll('.jerarca-trust:not([data-tb])');
  bars.forEach((bar) => {
    bar.dataset.tb = '1';
    bar.classList.add('jerarca-trust--anim');
    if (!('IntersectionObserver' in window)) {
      bar.classList.add('jerarca-trust--in');
      return;
    }
    const io = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('jerarca-trust--in');
            obs.unobserve(entry.target);
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
    );
    io.observe(bar);
  });
}

/**
 * Gold Sweep — unified across ALL section titles. A gold band sweeps across the
 * title each time it enters the viewport (re-triggers on scroll DOWN and UP).
 * One shared IntersectionObserver toggles .jerarca-gold-title--in on enter/leave.
 * Each title keeps its ORIGINAL color (captured into --gold-title-color) so the
 * sweep is safe on any background — light or dark. Reduced motion: no effect.
 */
const GOLD_TITLE_SELECTOR = [
  '.jerarca-editorial__title',
  '.jerarca-statement__heading',
  '.text-block h1',
  '.text-block h2',
  '.text-block h3',
  '.text-block h4',
].join(',');

/* Never sweep: the main hero "JERARCA" (keeps its tracking-in), badges, gate. */
const GOLD_TITLE_EXCLUDE = '.slide__content, .hero__content-wrapper, .jerarca-trust, .jerarca-gate';

let goldTitleObserver = null;

function initGoldTitles(root) {
  if (!motionOK.matches) return; /* reduced motion: titles stay static, no sweep */
  if (!goldTitleObserver && 'IntersectionObserver' in window) {
    goldTitleObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          /* add on enter, remove on leave → animation restarts every re-entry */
          entry.target.classList.toggle('jerarca-gold-title--in', entry.isIntersecting);
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.2 }
    );
  }
  if (!goldTitleObserver) return; /* no IO support: leave titles static */

  (root || document).querySelectorAll(GOLD_TITLE_SELECTOR).forEach((el) => {
    if (el.dataset.gt || el.closest(GOLD_TITLE_EXCLUDE) || !el.textContent.trim()) return;
    el.dataset.gt = '1';
    /* read color BEFORE writing classes (avoids layout thrash); keep each
       title's own color so white-on-light never happens */
    let c = '';
    try {
      c = getComputedStyle(el).color;
    } catch (e) {}
    if (c) el.style.setProperty('--gold-title-color', c);
    el.classList.add('jerarca-gold-title');
    goldTitleObserver.observe(el);
  });
}

function init(root) {
  initVideos(root);
  initTrustBar(root);
  if (fine.matches && motionOK.matches) {
    (root || document).querySelectorAll('.product-card:not([data-jt])').forEach(attach);
  }
}

/* Gold titles run getComputedStyle() over several headings (forces style
   recalc). They're below the fold, so defer their setup until the browser is
   idle — guarantees ZERO competition with the hero/LCP paint. */
function deferGoldTitles(root) {
  const run = () => initGoldTitles(root);
  if ('requestIdleCallback' in window) {
    requestIdleCallback(run, { timeout: 2000 });
  } else {
    setTimeout(run, 300);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

/* Defer gold-title setup until after first paint / idle. */
if (document.readyState === 'complete') {
  deferGoldTitles();
} else {
  window.addEventListener('load', () => deferGoldTitles(), { once: true });
}

/* Re-init for theme editor and dynamically rendered sections */
document.addEventListener('shopify:section:load', (e) => {
  init(e.target);
  deferGoldTitles(e.target);
});

/**
 * "Producto agregado" toast — visible confirmation when an item is added to
 * the cart. Listens for the theme's cart:update event but only fires on a real
 * add (source 'product-form-component'), never on quantity changes in the cart.
 */
let toastEl = null;
let toastTimer = 0;

function showCartToast() {
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'jerarca-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    toastEl.innerHTML =
      '<span class="jerarca-toast__check" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<path d="M5 12l4 4 10-10"/></svg></span>' +
      '<span class="jerarca-toast__text">Producto agregado</span>';
    document.body.appendChild(toastEl);
  }
  /* force reflow so re-triggering restarts the transition */
  toastEl.classList.remove('jerarca-toast--show');
  void toastEl.offsetWidth;
  toastEl.classList.add('jerarca-toast--show');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toastEl.classList.remove('jerarca-toast--show'), 2600);
}

document.addEventListener('cart:update', (e) => {
  const data = e.detail && e.detail.data;
  if (data && data.source === 'product-form-component' && !data.didError) {
    showCartToast();
  }
});
